import React, { useState, useEffect, useCallback } from 'react';
import { setToken as setLocalToken, currentLocalUser } from '../../lib/localAuth';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  RefreshCw,
  Loader2,
  Play,
  Square,
  UserPlus,
  Trash2,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  Eye,
  MailPlus,
  Copy,
} from 'lucide-react';
import { adminApi, ApiError } from '../../lib/api';
import { AdminUser as UserInfo } from '../../types/titan';

const UsersTab: React.FC = () => {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('Trader');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UserInfo | null>(null);
  const [impersonateTarget, setImpersonateTarget] = useState<UserInfo | null>(null);
  const [impersonating, setImpersonating] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Viewer');
  const [inviteHours, setInviteHours] = useState(72);
  const [inviteNote, setInviteNote] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [lastInvite, setLastInvite] = useState<null | {
    code: string;
    email: string;
    expires_at: string;
    email_sent: boolean;
  }>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.users();
      setUsers(data.users || []);
    } catch (e: any) {
      setError(e.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleControllerAction = async (uid: string, action: 'start' | 'stop') => {
    setActionLoading(`${uid}-${action}`);
    try {
      await adminApi.userAction(uid, action);
      await fetchUsers();
    } catch (e: any) {
      setError(e instanceof ApiError ? e.detail : (e.message || 'Request failed'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRoleChange = async (uid: string, role: string) => {
    setActionLoading(`${uid}-role`);
    try {
      await adminApi.updateRole(uid, role);
      await fetchUsers();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreate = async () => {
    if (!newEmail || !newPassword) return;
    setCreating(true);
    try {
      await adminApi.createUser({ email: newEmail, password: newPassword, role: newRole });
      setShowCreate(false);
      setNewEmail('');
      setNewPassword('');
      setNewRole('Trader');
      await fetchUsers();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateInvite = async () => {
    if (!inviteEmail) return;
    setCreatingInvite(true);
    setError(null);
    try {
      const result = await adminApi.createInvitation({
        email: inviteEmail,
        role: inviteRole as 'Admin' | 'Trader' | 'Analyst' | 'Viewer',
        expires_in_hours: inviteHours,
        note: inviteNote,
      });
      setLastInvite({
        code: result.code,
        email: result.email,
        expires_at: result.expires_at,
        email_sent: result.email_sent,
      });
    } catch (e: any) {
      setError(e.message || 'Failed to create invitation');
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(`${deleteTarget.uid}-delete`);
    try {
      await adminApi.deleteUser(deleteTarget.uid);
      setDeleteTarget(null);
      await fetchUsers();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleImpersonate = async () => {
    if (!impersonateTarget) return;
    setImpersonating(true);
    try {
      const data = await adminApi.impersonate(impersonateTarget.uid);
      const impersonationToken = data.token || data.custom_token;
      if (!impersonationToken) {
        throw new Error('Impersonation token was not returned by the server');
      }
      const adminUser = currentLocalUser();
      localStorage.setItem('titan_impersonation', JSON.stringify({
        admin_email: adminUser?.email || '',
        target_email: impersonateTarget.email,
        target_uid: impersonateTarget.uid,
        started_at: new Date().toISOString(),
      }));
      // Self-hosted JWT — swap the stored token for the impersonation token.
      setLocalToken(impersonationToken);
      setImpersonateTarget(null);
      window.location.href = '/portfolio';
    } catch (e: any) {
      setError(e.message || 'Impersonation failed');
      setImpersonating(false);
    }
  };

  const roleBadgeColor = (role: string) => {
    switch (role) {
      case 'Admin': return 'bg-red-500/10 text-red-600 border-red-500/30';
      case 'Trader': return 'bg-blue-500/10 text-blue-600 border-blue-500/30';
      case 'Analyst': return 'bg-purple-500/10 text-purple-600 border-purple-500/30';
      default: return 'bg-gray-500/10 text-gray-600 border-gray-500/30';
    }
  };

  const timeAgo = (iso?: string) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{users.length} registered user{users.length !== 1 ? 's' : ''}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchUsers}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          <Button variant="outline" size="sm" onClick={() => setShowInvite(true)}>
            <MailPlus className="w-4 h-4 mr-1" /> Invite
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}><UserPlus className="w-4 h-4 mr-1" /> Add User</Button>
        </div>
      </div>

      {error && (
        <Alert className="bg-red-500/10 border-red-500/30 text-red-600">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        {users.map(u => (
          <Card key={u.uid} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full bg-foreground/10 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  {u.email?.substring(0, 2).toUpperCase() || 'U'}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">{u.email || u.uid}</p>
                    {u.disabled && <Badge variant="outline" className="text-[10px] bg-red-500/10 text-red-600 border-red-500/30">Disabled</Badge>}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <Badge variant="outline" className={`text-[10px] ${roleBadgeColor(u.role)}`}>{u.role}</Badge>
                    {(u.controller?.scanner_online ?? u.controller?.running) ? (
                      <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-600 border-green-500/30">Running</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">Stopped</Badge>
                    )}
                    {(u.patterns ?? 0) > 0 && (
                      <span className="text-[11px] text-muted-foreground">{u.active_patterns}/{u.patterns} patterns</span>
                    )}
                    {u.controller?.positions ? (
                      <span className="text-[11px] text-muted-foreground">{u.controller.positions} pos</span>
                    ) : null}
                    {u.last_sign_in && (
                      <span className="text-[11px] text-muted-foreground/60">seen {timeAgo(u.last_sign_in)}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                {(u.controller?.scanner_online ?? u.controller?.running) ? (
                  <Button variant="outline" size="sm" className="text-red-600 border-red-500/30 hover:bg-red-500/10"
                    onClick={() => handleControllerAction(u.uid, 'stop')} disabled={actionLoading === `${u.uid}-stop`}>
                    {actionLoading === `${u.uid}-stop` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Square className="w-3 h-3" />}
                    <span className="ml-1 hidden sm:inline">Stop</span>
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" className="text-green-600 border-green-500/30 hover:bg-green-500/10"
                    onClick={() => handleControllerAction(u.uid, 'start')} disabled={actionLoading === `${u.uid}-start`}>
                    {actionLoading === `${u.uid}-start` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                    <span className="ml-1 hidden sm:inline">Start</span>
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setExpanded(expanded === u.uid ? null : u.uid)}>
                  {expanded === u.uid ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {expanded === u.uid && (
              <div className="mt-4 pt-3 border-t space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <p className="text-[11px] text-muted-foreground mb-1">Broker Credentials</p>
                    <div className="flex flex-wrap gap-1">
                      {[
                        ['alpaca', 'Stocks (Alpaca)'],
                        ['tradier', 'Stocks (Tradier)'],
                        ['oanda', 'Forex (OANDA)'],
                        ['coinbase', 'Crypto (Coinbase)'],
                      ].map(([key, label]) => {
                        const configured = Boolean((u.credentials || {})[key]);
                        return (
                          <Badge
                            key={key}
                            variant="outline"
                            className={`text-[10px] ${configured ? 'bg-green-500/10 text-green-600 border-green-500/30' : 'bg-gray-500/10 text-gray-400 border-gray-500/20'}`}
                          >
                            {configured ? <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> : <XCircle className="w-2.5 h-2.5 mr-0.5" />}
                            {label}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground mb-1">Controller</p>
                    <p className="text-sm">{u.controller ? u.controller.status : 'Not started'}</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground mb-1">Patterns</p>
                    <p className="text-sm">{u.active_patterns} active / {u.patterns} total</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground mb-1">Created</p>
                    <p className="text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'Unknown'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[11px] text-muted-foreground">Role:</p>
                  {['Admin', 'Trader', 'Analyst', 'Viewer'].map(role => (
                    <Button key={role} variant={u.role === role ? 'default' : 'outline'} size="sm" className="text-xs h-7"
                      onClick={() => role !== u.role && handleRoleChange(u.uid, role)} disabled={actionLoading === `${u.uid}-role`}>
                      {role}
                    </Button>
                  ))}
                  <div className="flex-1" />
                  <Button variant="outline" size="sm" className="text-amber-600 border-amber-500/30 hover:bg-amber-500/10 text-xs h-7"
                    onClick={() => setImpersonateTarget(u)}>
                    <Eye className="w-3 h-3 mr-1" /> Impersonate
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 border-red-500/30 hover:bg-red-500/10 text-xs h-7"
                    onClick={() => setDeleteTarget(u)}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground/50 font-mono">UID: {u.uid}</p>
              </div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription>Add a new user to the platform.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-sm font-medium">Email</label><Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="user@example.com" /></div>
            <div><label className="text-sm font-medium">Password</label><Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Minimum 6 characters" /></div>
            <div>
              <label className="text-sm font-medium">Role</label>
              <div className="flex gap-2 mt-1">
                {['Admin', 'Trader', 'Analyst', 'Viewer'].map(r => (
                  <Button key={r} variant={newRole === r ? 'default' : 'outline'} size="sm" onClick={() => setNewRole(r)}>{r}</Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !newEmail || !newPassword}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Invitation</DialogTitle>
            <DialogDescription>Generate and email an invitation code before user registration.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium">Invitee Email</label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="invitee@example.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Role on Registration</label>
              <div className="flex gap-2 mt-1 flex-wrap">
                {['Admin', 'Trader', 'Analyst', 'Viewer'].map(r => (
                  <Button key={r} variant={inviteRole === r ? 'default' : 'outline'} size="sm" onClick={() => setInviteRole(r)}>{r}</Button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Expires In (hours)</label>
              <Input
                type="number"
                min={1}
                max={720}
                value={inviteHours}
                onChange={e => setInviteHours(parseInt(e.target.value, 10) || 72)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Note (optional)</label>
              <Input
                value={inviteNote}
                onChange={e => setInviteNote(e.target.value)}
                placeholder="Internal note for this invitation"
              />
            </div>
            {lastInvite && (
              <Alert className="bg-blue-500/10 border-blue-500/30">
                <AlertDescription>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground">Invitation created for {lastInvite.email}</div>
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-sm">{lastInvite.code}</code>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => navigator.clipboard.writeText(lastInvite.code)}
                      >
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Expires: {new Date(lastInvite.expires_at).toLocaleString()} • Email sent: {lastInvite.email_sent ? 'Yes' : 'No'}
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)}>Close</Button>
            <Button onClick={handleCreateInvite} disabled={creatingInvite || !inviteEmail}>
              {creatingInvite ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <MailPlus className="w-4 h-4 mr-1" />}
              Create & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>Are you sure you want to delete <strong>{deleteTarget?.email}</strong>? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actionLoading?.includes('delete')}>
              {actionLoading?.includes('delete') ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />} Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!impersonateTarget} onOpenChange={() => setImpersonateTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Impersonate User</DialogTitle>
            <DialogDescription>
              You will be signed in as <strong>{impersonateTarget?.email}</strong> and see the platform from their perspective.
              You can return to your admin account using the banner at the top of the page.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Alert className="bg-amber-500/10 border-amber-500/30 text-amber-700">
              <AlertDescription className="text-sm">
                This is an audited action. All activity during impersonation is logged.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpersonateTarget(null)}>Cancel</Button>
            <Button onClick={handleImpersonate} disabled={impersonating} className="bg-amber-600 hover:bg-amber-700 text-white">
              {impersonating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
              Impersonate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersTab;
